import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface EvidenceTypeOption {
  id: number
  name: string
  description: string | null
}

interface EvidenceItem {
  id: number
  evidence_type_name: string | null
  evidence_date: string | null
  quantity: number | null
  filename: string | null
  user_name: string | null
  status: number
  rating: number | null
}

interface Props {
  case_record: CaseRecord
  evidence: EvidenceItem[]
  evidence_types: EvidenceTypeOption[]
  current_type: string
}

const STATUS_LABELS: Record<number, string> = {
  0: "Active",
  20: "Received",
}

const STATUS_STYLES: Record<number, string> = {
  0: "bg-hpi-pale-gray text-hpi-dark-gray",
  20: "bg-hpi-dark-green/20 text-hpi-dark-green",
}

function formatQuantity(qty: number | null): string {
  if (qty === null || qty === undefined) return ""
  if (qty > 1000) return `${(qty / 1000).toFixed(2)} gb`
  return `${qty.toFixed(2)} mb`
}

function ratingStars(rating: number | null): string {
  if (rating === null || rating === undefined) return "-"
  return "\u2605".repeat(rating) + "\u2606".repeat(5 - rating)
}

export default function Index({
  case_record,
  evidence,
  evidence_types,
  current_type,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Evidence", href: `/cases/${case_record.id}/evidence` },
  ]

  const basePath = `/cases/${case_record.id}/evidence`

  const typeFilters = [
    { key: "all", label: "All" },
    ...evidence_types.map((t) => ({ key: String(t.id), label: t.name })),
  ]

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this evidence?")) {
      router.delete(`${basePath}/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Evidence - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Evidence for {case_record.display_name}
          </h1>
          <Link
            href={`${basePath}/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Evidence
          </Link>
        </div>

        <div className="mb-4 flex items-center gap-2">
          <span className="text-hpi-med-gray text-sm">Type:</span>
          {typeFilters.map((f) => (
            <Link
              key={f.key}
              href={f.key === "all" ? basePath : `${basePath}?type=${f.key}`}
              className={`rounded px-3 py-1 text-sm ${
                current_type === f.key
                  ? "bg-hpi-dark-blue text-white"
                  : "bg-hpi-pale-gray text-hpi-dark-gray hover:bg-hpi-pale-gray"
              }`}
            >
              {f.label}
            </Link>
          ))}
        </div>

        {evidence.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Quantity
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  File
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Investigator
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Rating
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Status
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {evidence.map((ev) => (
                <tr key={ev.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                    {formatDate(ev.evidence_date)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {ev.evidence_type_name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {formatQuantity(ev.quantity)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`${basePath}/${ev.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue"
                    >
                      {ev.filename ?? `Evidence #${ev.id}`}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {ev.user_name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {ratingStars(ev.rating)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLES[ev.status] ?? "bg-hpi-pale-gray text-hpi-dark-gray"}`}
                    >
                      {STATUS_LABELS[ev.status] ?? "Unknown"}
                    </span>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                    <Link
                      href={`${basePath}/${ev.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      View
                    </Link>
                    <Link
                      href={`${basePath}/${ev.id}/edit`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(ev.id)}
                      className="text-hpi-red hover:text-hpi-red"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No evidence found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
